/* eslint-disable react/jsx-wrap-multilines */
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ContentSection, Fieldset, Layout, PageWrap, SectionHeading } from '@vwfs-bronson/bronson-react';
import { Formik, FormikHelpers } from 'formik';
import { ObjectSchema } from 'yup';
import { getBrandName } from '../../../config';
import { getCustomer } from '../../../services/api/miles/miles-customer-services';
import {
  createCoCustomerAndLinkToTheQuote,
  getQuoteCoCustomers,
  unlinkCoCustomerFromTheQuote,
} from '../../../services/api/miles/miles-services';
import { SetState } from '../../../services/common/miscUtils.types';
import {
  customerSelectors,
  setCurrentCoCustomerId,
  setHasCoCustomer,
  setMissingMilesData,
} from '../../../services/redux/features/customer.slice';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import { quoteSelectors } from '../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { ProspectSearchPopupModal } from '../../ProspectSearchPopupModal/ProspectSearchPopupModal';
import { validateEmailTls } from '../utils/checkTLSUtil';
import { ConsentModal } from './ConsentModal';
import {
  BirthName,
  CountryCodeMobilePhone,
  CustomerRelations,
  DateOfBirth,
  Email,
  FirstName,
  Gender,
  LastName,
  MobilePhone,
  Profession,
  TelephonePhone,
} from './fieldset';
import { getPrivateCoCustomerFormInitialValues, PrivateCoCustomerProspectForm } from './initialValues';
import { ProspectPageButtons } from './ProspectPageButtons';
import { getCreateCoCustomerRequest, getIsCoCustomerLinkedToQuote } from './utils';
import { getPrivateCoCustomerValidationSchema } from './validationSchema';

interface Props {
  initialMilesCustomerId: string | undefined;
  quoteId: string;
}

export const CoCustomerProspectForm: FunctionComponent<Props> = ({ initialMilesCustomerId, quoteId }) => {
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [initialValues, setInitialValues] = useState({} as PrivateCoCustomerProspectForm);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<PrivateCoCustomerProspectForm>>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [milesCustomerId, setMilesCustomerId] = useState(() => initialMilesCustomerId);
  const [isCoCustomerLinkedToQuote, setIsCoCustomerLinkedToQuote] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const missingMilesData = useSelector(customerSelectors.selectMissingMilesData);
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);
  const resetFormButtonActive = isQuoteEditable && !!milesCustomerId;

  useEffect(() => {
    if (isSubmitted) return;

    dispatch(loadingActions.setLoading(true));
    (async () => {
      let customerData = {} as CustomerIntegration.Schemas.CustomerDataByIdResponse;
      let milesCoCustomerRelationType: QuoteSchema.Schemas.MilesCoCustomerRelationType | undefined;

      if (milesCustomerId) {
        customerData = (await getCustomer(milesCustomerId)).customerData;
        const quoteCoCustomers = await getQuoteCoCustomers(quoteId);
        milesCoCustomerRelationType =
          quoteCoCustomers.customerData?.customerRelations?.[0]?.milesCoCustomerRelationType;

        const isCoCustomerLinked: boolean = getIsCoCustomerLinkedToQuote(
          quoteCoCustomers?.customerData,
          milesCustomerId!
        );

        setIsCoCustomerLinkedToQuote(isCoCustomerLinked);
      }

      setInitialValues(
        getPrivateCoCustomerFormInitialValues(
          customerData,
          milesCoCustomerRelationType,
          milesCustomerId ? missingMilesData : undefined
        )
      );
      dispatch(loadingActions.setLoading(false));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milesCustomerId]);

  useEffect(() => {
    if (isQuoteEditable) {
      setValidationSchema(getPrivateCoCustomerValidationSchema(t));
    } else {
      setValidationSchema(undefined);
    }
  }, [t, isQuoteEditable]);

  const onSubmit = async (
    values: PrivateCoCustomerProspectForm,
    { setFieldError }: FormikHelpers<PrivateCoCustomerProspectForm>
  ) => {
    const tlsValid = await validateEmailTls(values.email!, setFieldError, dispatch);

    if (!tlsValid) {
      return;
    }

    if (!isQuoteEditable) {
      navigate.form('/3');
      return;
    }

    dispatch(loadingActions.setLoading(true));

    try {
      const createCoCustomerRequest = getCreateCoCustomerRequest(values, milesCustomerId);

      const milesCoCustomerRelationTypeChanged = initialValues.customerRelations !== values.customerRelations;
      if (
        initialValues.customerRelations &&
        milesCoCustomerRelationTypeChanged &&
        milesCustomerId &&
        isCoCustomerLinkedToQuote
      ) {
        await unlinkCoCustomerFromTheQuote(quoteId, milesCustomerId);
      }

      const newMilesCustomerId = (await createCoCustomerAndLinkToTheQuote(createCoCustomerRequest, quoteId))
        .milesCustomerId;

      dispatch(setCurrentCoCustomerId(newMilesCustomerId));
      dispatch(
        setMissingMilesData({
          milesCoCustomerRelationType: values.customerRelations,
          posFrOccupationCoCustomer: values.profession,
          hasCoCustomer: true,
        })
      );
      setIsSubmitted(true);

      setShowConsentModal(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      navigate.error();
    } finally {
      dispatch(loadingActions.setLoading(false));
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <PageWrap>
        <ContentSection altBackground={getBrandName().includes('vw')} className="u-pv-none">
          {/* eslint-disable-next-line no-use-before-define */}
          <FormContent
            milesCustomerId={milesCustomerId}
            setMilesCustomerId={setMilesCustomerId}
            resetFormButtonActive={resetFormButtonActive}
            quoteId={quoteId}
            isCoCustomerLinkedToQuote={isCoCustomerLinkedToQuote}
            setIsCoCustomerLinkedToQuote={setIsCoCustomerLinkedToQuote}
          />
          <Layout center>
            <ProspectPageButtons isCoCustomer />
          </Layout>
        </ContentSection>
        <ConsentModal visible={showConsentModal} setShowConsentModal={setShowConsentModal} isCoCustomer hasCoCustomer />
      </PageWrap>
    </Formik>
  );
};

interface FormContentProps {
  setMilesCustomerId: SetState<string | undefined>;
  resetFormButtonActive: boolean;
  milesCustomerId?: string;
  quoteId: string;
  isCoCustomerLinkedToQuote: boolean;
  setIsCoCustomerLinkedToQuote: SetState<boolean>;
}

const FormContent: FunctionComponent<FormContentProps> = ({
  setMilesCustomerId,
  milesCustomerId,
  resetFormButtonActive,
  quoteId,
  isCoCustomerLinkedToQuote,
  setIsCoCustomerLinkedToQuote,
}) => {
  const [showProspectSearchModal, setShowProspectSearchModal] = useState(false);
  const { t } = useTranslation();

  const onConfirmPopup = async ({ milesCustomerId }) => {
    setMilesCustomerId(milesCustomerId);
  };

  const isCoCustomerSelected = Boolean(milesCustomerId);
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);
  const dispatch = useDispatch();

  return (
    <ContentSection.ComponentWrapper className="u-mt">
      <div className="u-mb-small@s">
        <Layout>
          <Layout.Item default="3/4" m="2/3" s="1/1">
            <SectionHeading level={2} className="u-mb-xsmall@s">
              {t('step2:heading:coCustomer')}
            </SectionHeading>
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <Layout>
              <Layout.Item default="1/2" s="2/5" className="u-text-center">
                <Layout.Item>
                  <Button
                    key="b2"
                    icon="semantic-search"
                    ariaLabel="search"
                    secondary
                    type="button"
                    onClick={() => {
                      setShowProspectSearchModal(true);
                    }}
                    disabled={!isQuoteEditable}
                  />
                </Layout.Item>
                <Layout.Item default="1/1">{t('step2:prospectPopup:buttonTitle')}</Layout.Item>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/2" className="u-text-center">
                <Layout.Item>
                  <Button
                    key="b2"
                    icon="semantic-delete"
                    ariaLabel="delete"
                    secondary
                    type="button"
                    onClick={async () => {
                      if (isCoCustomerLinkedToQuote) {
                        await unlinkCoCustomerFromTheQuote(quoteId, milesCustomerId!);
                        setIsCoCustomerLinkedToQuote(false);
                      }
                      setMilesCustomerId(undefined);
                      dispatch(setHasCoCustomer(false));
                      dispatch(setCurrentCoCustomerId(''));
                    }}
                    disabled={!resetFormButtonActive}
                  />
                </Layout.Item>
                <Layout.Item>{t('step2:resetForm:buttonTitle')}</Layout.Item>
              </Layout.Item>
            </Layout>

            <ProspectSearchPopupModal
              visible={showProspectSearchModal ?? false}
              onCancel={() => {
                setShowProspectSearchModal(false);
              }}
              onConfirm={onConfirmPopup}
              cocontractor
            />
          </Layout.Item>
        </Layout>
      </div>

      <Fieldset disabled={!isQuoteEditable}>
        <Fieldset.Row>
          <Layout className="u-mv-small">
            <Layout.Item default="1/2" s="1/1">
              <Gender />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <CustomerRelations />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <FirstName isDisabled={isCoCustomerSelected} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <LastName isDisabled={isCoCustomerSelected} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <BirthName isDisabled={isCoCustomerSelected} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <DateOfBirth
                infoText={t('step2:dateInfoText:label')}
                labelText={t('step2:dateOfBirth:label')}
                disabled={isCoCustomerSelected}
              />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <Profession />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <Email />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <CountryCodeMobilePhone />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <MobilePhone />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <TelephonePhone />
            </Layout.Item>
          </Layout>
        </Fieldset.Row>
      </Fieldset>
    </ContentSection.ComponentWrapper>
  );
};
